'use client';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import WhatsAppFloat from '@/components/WhatsAppFloat';

import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  MapPin,
  Plane,
  ShieldCheck,
  Star,
  Users,
  BriefcaseBusiness,
  Car,
  Route,
  CalendarCheck,
  MessageCircle,
  ChevronDown,
  Building2,
  Compass,
  BookOpenText,
  KeyRound,
} from 'lucide-react';

const WA_NUMBER = '6287786556067';

const createWhatsAppUrl = (vehicle = '') => {
  const message = `Halo NAYVERA INDO TRANSPORT,

Saya ingin menanyakan ketersediaan sewa mobil Semarang.

DATA PEMESANAN

Nama pemesan:
Kendaraan yang diminati: ${vehicle}
Jenis sewa: Dengan Sopir / Lepas Kunci
Tanggal mulai:
Jam mulai:
Tanggal selesai:
Jam selesai:
Lokasi penjemputan:
Tujuan / rute:
Jumlah penumpang:

Mohon informasi mengenai:
• Ketersediaan kendaraan
• Harga dan pilihan paket
• Fasilitas yang didapat
• Syarat dan ketentuan
• Biaya tambahan jika ada

Terima kasih.`;

  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`;
};

const fleet = [
  {
    name: 'Toyota Agya / Daihatsu Ayla',
    category: 'City Car',
    status: 'Tersedia',
    transmission: 'Matic / Manual',
    fuel: 'Bensin',
    capacity: '5 Penumpang',
    image: '/assets/images/AYLA DAN AGYA.jpg',
    features: ['Power Steering', 'AC Dingin', 'Audio Bluetooth', 'Eco Mode'],
    price: 'Rp 290.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'Pilihan hemat dan praktis untuk menjelajahi spot kuliner Kota Lama dan perjalanan harian di Semarang.',
  },
  {
    name: 'Toyota Avanza',
    category: 'Family MPV',
    status: 'Tersedia',
    transmission: 'Matic / Manual',
    fuel: 'Bensin',
    capacity: '7 Penumpang',
    image: '/assets/images/Avanza.jpeg',
    features: ['AC Double Blower', 'Audio Sistem', 'Kabin Luas', '+1 lagi'],
    price: 'Rp 598.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'Mobil MPV keluarga ideal untuk trip wisata kota Semarang, kunjungan keluarga, hingga perjalanan dinas.',
  },
  {
    name: 'Toyota Innova Reborn',
    category: 'Family & Business',
    status: 'Tersedia',
    transmission: 'Matic',
    fuel: 'Diesel / Bensin',
    capacity: '7 Penumpang',
    image: '/assets/images/REBORN.jpeg',
    features: ['AC Double Blower', 'Kabin Nyaman', 'Captain Seat opt', '+1 lagi'],
    price: 'Rp 750.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'Kabin tenang, tangguh, dan bertenaga untuk rute menanjak ke kawasan Bandungan maupun perjalanan luar kota.',
  },
  {
    name: 'Toyota Innova Zenix',
    category: 'Executive MPV',
    status: 'Tersedia',
    transmission: 'Matic',
    fuel: 'Bensin / Hybrid',
    capacity: '7 Penumpang',
    image: '/assets/images/ZENIX G.webp',
    features: ['Panoramic Roof', 'Captain Seat', 'Electric Parking', '+1 lagi'],
    price: 'Rp 850.000',
    priceLabel: '12 jam · lepas kunci',
    description: 'Kendaraan berteknologi modern untuk memberikan kenyamanan kelas atas bagi eksekutif dan keluarga.',
  },
  {
    name: 'Toyota Fortuner',
    category: 'Premium SUV',
    status: 'Tersedia',
    transmission: 'Matic',
    fuel: 'Diesel',
    capacity: '7 Penumpang',
    image: '/assets/images/Fortuner.jpeg',
    features: ['4x2 / 4x4', 'Leather Seat', 'Push Start Button', '+1 lagi'],
    price: 'Rp 1.700.000',
    priceLabel: '12 jam · dengan driver',
    description: 'SUV gagah dan elegan cocok untuk perjalanan bisnis VIP, kunjungan instansi, serta medan jalan menanjak.',
  },
  {
    name: 'Toyota Hiace Premio',
    category: 'Premium Van',
    status: 'Tersedia',
    transmission: 'Manual',
    fuel: 'Diesel',
    capacity: '14 Penumpang',
    image: '/assets/images/Hiace-Premio.jpeg',
    features: ['High Roof', 'AC per Kepala', 'Reclining Seat', '+1 lagi'],
    price: 'Rp 1.850.000',
    priceLabel: '12 jam · dengan driver',
    description: 'Pilihan terbaik untuk wisata rombongan instansi, gathering kantor, atau perjalanan keluarga besar di Semarang.',
  },
];

const services = [
  {
    icon: KeyRound,
    title: 'Sewa Mobil Lepas Kunci Semarang',
    description:
      'Solusi berkendara mandiri di Semarang untuk Anda yang menginginkan fleksibilitas penuh. Persyaratan verifikasi cepat dan transparan.',
  },
  {
    icon: Users,
    title: 'Sewa Mobil dengan Driver',
    description:
      'Nikmati wisata tanpa lelah ditemani pengemudi profesional yang menguasai jalanan Semarang bawah maupun area dataran tinggi.',
  },
  {
    icon: Plane,
    title: 'Antar Jemput Bandara & Stasiun',
    description:
      'Layanan antar-jemput tepat waktu dari Bandara Jenderal Ahmad Yani maupun Stasiun Tawang dan Poncol Semarang.',
  },
  {
    icon: BriefcaseBusiness,
    title: 'Transportasi Bisnis & Perusahaan',
    description:
      'Dukungan kendaraan prima untuk agenda meeting bisnis, kunjungan proyek industri di Simongan/Tugu, dan acara instansi.',
  },
  {
    icon: Route,
    title: 'Carter Antarkota via Tol Trans-Jawa',
    description:
      'Layanan antar kota dari Semarang menuju Solo, Yogyakarta, Surabaya, Cirebon, atau Jakarta secara cepat dan aman.',
  },
  {
    icon: Compass,
    title: 'Wisata Kota Lama & Bandungan',
    description:
      'Eksplorasi destinasi ikonik Semarang seperti Kota Lama, Lawang Sewu, Sam Poo Kong, Gedong Songo, hingga kuliner Pandanaran.',
  },
];

const useCases = [
  {
    icon: CalendarCheck,
    title: 'Wisata Kuliner & Sejarah',
    description:
      'Pilihan fleksibel berburu Lumpia, Tahu Baxo, dan menjelajahi keindahan arsitektur Kota Lama Semarang.',
  },
  {
    icon: Building2,
    title: 'Agenda Kerja & Dinas Kantor',
    description:
      'Mendukung mobilitas rapat instansi pemerintah di area Simpang Lima serta perkantoran di Semarang.',
  },
  {
    icon: Plane,
    title: 'Penjemputan Bandara / Stasiun',
    description:
      'Driver kami siap menyambut kedatangan Anda di pintu keluar Bandara Ahmad Yani atau Stasiun Tawang tepat waktu.',
  },
  {
    icon: Users,
    title: 'Rombongan & Gathering Kantor',
    description:
      'Tersedia armada Hiace berkapasitas 14 orang untuk agenda kegiatan wisata rombongan atau study tour.',
  },
  {
    icon: Star,
    title: 'Trip Wisata Dataran Tinggi',
    description:
      'Perjalanan nyaman menembus kontur tanjakan kawasan Umbul Sidomukti dan Pegunungan Ungaran.',
  },
  {
    icon: Route,
    title: 'Perjalanan Lintas Kota',
    description:
      'Layanan travel pribadi langsung berangkat dari Semarang via Tol Trans-Jawa menuju Joglosemar maupun Jawa Timur.',
  },
];

const areaGroups = [
  {
    title: 'Semarang Kota',
    description: 'Cakupan cepat di pusat perkotaan.',
    places: [
      'Semarang Tengah (Simpang Lima)',
      'Semarang Selatan',
      'Semarang Barat',
      'Semarang Timur',
      'Semarang Utara (Kota Lama)',
      'Gajahmungkur & Candisari',
    ],
  },
  {
    title: 'Semarang Atas & Kabupaten',
    description: 'Menjangkau kawasan sejuk & wisata.',
    places: [
      'Banyumanik',
      'Tembalang (Undip)',
      'Ungaran (Kab. Semarang)',
      'Bandungan',
      'Ambarawa',
      'Kawasan Industri Tugu & Genuk',
    ],
  },
  {
    title: 'Hub Transportasi Utama',
    description: 'Akses penjemputan utama.',
    places: [
      'Bandara Jenderal Ahmad Yani',
      'Stasiun Semarang Tawang',
      'Stasiun Semarang Poncol',
      'Terminal Terboyo / Mangkang',
      'Gerbang Tol Muktiharjo',
      'Gerbang Tol Banyumanik',
    ],
  },
  {
    title: 'Antarkota dari Semarang',
    description: 'Akses mudah ke kota tetangga.',
    places: [
      'Solo / Surakarta',
      'Yogyakarta via Tol Bawen',
      'Kudus & Demak',
      'Magelang / Borobudur',
      'Pekalongan & Tegal',
      'Surabaya via Tol Trans-Jawa',
    ],
  },
];

const reasons = [
  {
    icon: Car,
    title: 'Armada Terawat & Bersih',
    text: 'Seluruh kendaraan rutin dicek dan dijaga kebersihannya agar selalu nyaman menghadapi rute kota maupun tanjakan.',
  },
  {
    icon: MessageCircle,
    title: 'Konsultasi Mudah via WhatsApp',
    text: 'Respon cepat dari admin kami untuk membahas penyesuaian rute, jadwal pemesanan, dan budget sewa.',
  },
  {
    icon: MapPin,
    title: 'Driver Paham Rute Semarang',
    text: 'Pengemudi berpengalaman yang menguasai rute Semarang Bawah, Semarang Atas, hingga lokasi kuliner favorit.',
  },
  {
    icon: ShieldCheck,
    title: 'Tarif Transparan Tanpa Biaya Tersembunyi',
    text: 'Kejelasan harga dan rincian fasilitas disampaikan secara detail sejak awal konfirmasi.',
  },
];

const articles = [
  {
    number: '01',
    category: 'Panduan Wisata',
    title: 'Tips Memilih Sewa Mobil Semarang untuk Liburan Keluarga ke Kota Lama & Bandungan',
    paragraphs: [
      'Semarang memiliki daya tarik wisata yang sangat lengkap, mulai dari kawasan bersejarah Kota Lama di Semarang Bawah hingga panorama pegunungan yang sejuk di kawasan Bandungan dan Ungaran.',
      'Perbedaan kontur jalanan antara area perkotaan yang datar dengan area Semarang Atas yang menanjak memerlukan kendaraan dengan kondisi mesin yang prima. Memilih sewa mobil dengan sopir di Semarang dari NAYVERA membuat perjalanan keluarga Anda menjadi jauh lebih santai.',
      'Pengemudi kami tidak hanya mengantar Anda ke destinasi favorit seperti Lawang Sewu dan Sam Poo Kong, tetapi juga merekomendasikan pusat oleh-oleh Lumpia dan Tahu Baxo terbaik.',
    ],
  },
  {
    number: '02',
    category: 'Solusi Korporat',
    title: 'Sewa Mobil Semarang untuk Perjalanan Bisnis, Rapat Instansi & Industri',
    paragraphs: [
      'Sebagai ibu kota Provinsi Jawa Tengah, Semarang merupakan pusat kegiatan pemerintahan, pendidikan, dan kawasan industri manufaktur besar di Jawa Tengah.',
      'NAYVERA INDO TRANSPORT menyediakan armada kelas atas seperti Toyota Innova Reborn, Zenix, hingga Fortuner yang menjamin kesan elegan dan profesionalisme untuk menjamu tamu korporat.',
      'Dengan pengemudi yang disiplin waktu dan rapi, agenda perjalanan dinas serta rapat kerja Anda di Kota Semarang dapat berjalan lancar.',
    ],
  },
  {
    number: '03',
    category: 'Lepas Kunci vs Sopir',
    title: 'Perbedaan Sewa Mobil Lepas Kunci dan Dengan Driver di Semarang',
    paragraphs: [
      'Opsi lepas kunci sangat disukai oleh warga lokal Semarang maupun mahasiswa yang menginginkan kebebasan bergerak dengan privasi terjaga.',
      'Sementara itu, bagi wisatawan luar kota yang mendarat di Bandara Ahmad Yani atau Stasiun Tawang, memilih paket sewa mobil dengan sopir adalah solusi paling praktis untuk menghindari stres navigasi rute satu arah (one-way) di jalanan utama Semarang.',
      'Diskusikan kebutuhan perjalanan Anda bersama tim NAYVERA agar kami dapat memberikan saran paket sewa yang paling efisien.',
    ],
  },
  {
    number: '04',
    category: 'Airport & Stasiun',
    title: 'Panduan Antar Jemput Bandara Ahmad Yani & Stasiun Tawang Semarang',
    paragraphs: [
      'Akses transportasi utama masuk ke Semarang didominasi oleh penerbangan di Bandara Internasional Jenderal Ahmad Yani serta rute kereta api di Stasiun Tawang dan Poncol.',
      'Menggunakan layanan antar-jemput khusus dari NAYVERA menghindarkan Anda dari antrean panjang kendaraan di area kedatangan stasiun atau bandara.',
      'Cukup informasikan nomor penerbangan atau jadwal tiba kereta Anda, pengemudi kami akan siap menunggu dan membantu membawa barang bawaan Anda ke dalam mobil.',
    ],
  },
  {
    number: '05',
    category: 'Rekomendasi Rombongan',
    title: 'Sewa Hiace Premio di Semarang untuk Outing Kantor & Tour Joglosemar',
    paragraphs: [
      'Bepergian bersama rombongan kantor atau keluarga besar dalam rute wisata Joglosemar (Jogja-Solo-Semarang) akan lebih berkesan saat semua peserta ada di dalam satu kendaraan.',
      'Toyota Hiace Premio yang kami sediakan memiliki kelapangan kabin luar biasa, AC dingin di setiap baris penutup kepala, serta kursi empuk yang nyaman untuk perjalanan jauh.',
      'Hubungi tim kami untuk mendiskusikan rencana titik kumpul dan rute tempat wisata agar persiapan unit van besar dapat dilakukan secara optimal.',
    ],
  },
  {
    number: '06',
    category: 'Tips Pemesanan',
    title: 'Langkah Mudah Booking Rental Mobil di NAYVERA INDO TRANSPORT Semarang',
    paragraphs: [
      'Pemesanan armada di NAYVERA dibuat sangat fleksibel melalui platform WhatsApp tanpa perlu mengisi form rumit.',
      'Kirimkan jadwal pemakaian, jam jemput, alamat titik lokasi di Semarang, serta jenis armada yang diminati.',
      'Customer service kami akan langsung memberikan jawaban ketersediaan kendaraan beserta detail rincian tarif transparan.',
    ],
  },
];

const faqs = [
  {
    question: 'Berapa kisaran harga sewa mobil di Semarang bersama NAYVERA?',
    answer:
      'Harga sewa sangat terjangkau mulai dari 300 ribuan tergantung tipe kendaraan, durasi sewa, serta pilihan sewa lepas kunci atau dengan pengemudi.',
  },
  {
    question: 'Apakah tersedia layanan rental mobil lepas kunci di Semarang?',
    answer:
      'Ya, kami menyediakan opsi sewa lepas kunci untuk tipe unit tertentu dengan syarat kelengkapan verifikasi dokumen identitas yang berlaku.',
  },
  {
    question: 'Apakah bisa dijemput langsung di Bandara Ahmad Yani atau Stasiun Tawang?',
    answer:
      'Sangat bisa. Kami melayani antar-jemput dari Bandara Ahmad Yani, Stasiun Tawang, maupun Stasiun Poncol secara on-time.',
  },
  {
    question: 'Wilayah mana saja yang masuk dalam jangkauan sewa mobil Semarang?',
    answer:
      'Layanan kami mencakup Semarang Kota, Semarang Atas (Banyumanik/Tembalang), Kabupaten Semarang (Ungaran/Bandungan/Ambarawa), hingga kawasan industri.',
  },
  {
    question: 'Apakah mobil bisa digunakan untuk perjalanan ke Solo, Jogja, atau Surabaya?',
    answer:
      'Bisa sekali. Armada kami siap melayani perjalanan antarkota lintas provinsi via Tol Trans-Jawa.',
  },
  {
    question: 'Apakah kuat untuk jalur menanjak ke kawasan wisata Bandungan?',
    answer:
      'Tentu saja. Seluruh armada kami rutin mendapatkan servis berkala dan selalu dalam kondisi prima untuk melewati tanjakan.',
  },
  {
    question: 'Bagaimana cara konfirmasi booking mobil?',
    answer:
      'Hubungi kami via WhatsApp, sampaikan format data pemesanan (tanggal, armada, rute), dan admin kami akan memberikan konfirmasi ketersediaannya.',
  },
  {
    question: 'Apakah biaya BBM, tol, dan parkir sudah termasuk dalam tarif?',
    answer:
      'Bergantung pada jenis paket sewa yang Anda pilih. Rincian apakah paket termasuk BBM, tol, dan parkir akan dijelaskan secara terbuka oleh admin.',
  },
];

export default function SewaMobilSemarangPage() {
  const whatsappUrl = createWhatsAppUrl();

  return (
    <main className="min-h-screen bg-white text-slate-900">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#07111f]">
        <div className="absolute inset-0">
          <img
            src="/assets/images/AYLA DAN AGYA.jpg"
            alt="Sewa mobil Semarang NAYVERA INDO TRANSPORT"
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#07111f] via-[#07111f]/95 to-[#07111f]/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#07111f] via-transparent to-transparent" />
        </div>

        <div className="relative mx-auto grid min-h-[680px] max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-[1.15fr_.85fr] lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#d4af37]/40 bg-[#d4af37]/10 px-4 py-2 text-sm font-medium text-[#e4c45a]">
              <MapPin size={16} />
              Sewa Mobil Semarang & Sekitarnya
            </div>

            <h1 className="text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              Sewa Mobil Semarang
              <span className="mt-2 block text-[#d4af37]">
                Mulai dari 300 Ribuan Terpercaya
              </span>
            </h1>

            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">
              NAYVERA INDO TRANSPORT menyediakan solusi rental mobil profesional
              di Semarang untuk kebutuhan wisata Kota Lama & Bandungan, perjalanan dinas,
              penjemputan bandara/stasiun, hingga carter antarkota via Tol Trans-Jawa.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#d4af37] px-6 py-3.5 font-semibold text-[#07111f] transition hover:bg-[#e4c45a]"
              >
                <MessageCircle size={19} />
                Konsultasi & Booking WhatsApp
                <ArrowRight size={18} />
              </a>

              <a
                href="#armada"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-6 py-3.5 font-semibold text-white backdrop-blur transition hover:border-[#d4af37]/60 hover:bg-white/10"
              >
                Lihat Pilihan Armada
              </a>
            </div>

            <div className="mt-10 grid max-w-3xl grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                ['300 Ribuan', 'Tarif terjangkau*'],
                ['Lepas Kunci', 'Opsi fleksibel'],
                ['Dengan Sopir', 'Driver berpengalaman'],
                ['Semarang Raya', 'Cakupan luas'],
              ].map(([value, label]) => (
                <div
                  key={label}
                  className="border-l border-[#d4af37]/50 pl-4"
                >
                  <div className="font-bold text-[#e4c45a]">{value}</div>
                  <div className="mt-1 text-xs leading-5 text-slate-400">
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:block">
            <div className="rounded-3xl border border-white/10 bg-white/[0.06] p-7 shadow-2xl backdrop-blur">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#d4af37]/15 text-[#e4c45a]">
                  <Car size={25} />
                </div>

                <div>
                  <p className="font-bold text-white">Rencana Perjalanan di Semarang?</p>
                  <p className="text-sm text-slate-400">
                    Konsultasikan kebutuhan Anda sekarang
                  </p>
                </div>
              </div>

              <div className="mt-7 space-y-4">
                {[
                  'Tentukan tanggal dan durasi sewa',
                  'Pilih armada sesuai jumlah penumpang',
                  'Tentukan titik jemput di Semarang',
                  'Konfirmasikan harga & fasilitas',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2
                      size={19}
                      className="mt-0.5 shrink-0 text-[#d4af37]"
                    />
                    <p className="text-sm leading-6 text-slate-300">
                      {item}
                    </p>
                  </div>
                ))}
              </div>

              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#d4af37] px-5 py-3.5 font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
              >
                <MessageCircle size={18} />
                Tanyakan Ketersediaan Unit
              </a>

              <p className="mt-4 text-center text-xs leading-5 text-slate-400">
                Respon cepat via WhatsApp oleh tim NAYVERA.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-white py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-[1.1fr_.9fr] lg:px-8">
          <div>
            <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Pusat Rental Mobil Semarang
            </p>

            <h2 className="text-3xl font-bold leading-tight text-[#07111f] sm:text-4xl">
              Solusi Transportasi Terbaik di
              <span className="text-[#b89528]"> Kota & Kabupaten Semarang</span>
            </h2>

            <p className="mt-6 text-base leading-8 text-slate-600">
              Mencari layanan rental mobil yang aman, terawat, dan memuaskan di Semarang? 
              NAYVERA INDO TRANSPORT hadir memberikan kemudahan transportasi bagi wisatawan 
              maupun pelaku bisnis yang berkunjung ke Kota Jamu. Mulai dari city car yang lincah 
              hingga MPV keluarga dan van berkapasitas besar tersedia lengkap.
            </p>

            <p className="mt-4 text-base leading-8 text-slate-600">
              Nikmati kebebasan memilih sewa mobil lepas kunci untuk berkendara mandiri, 
              atau manfaatkan opsi dengan sopir profesional yang berpengalaman menguasai 
              jalanan Semarang Bawah hingga area pegunungan Ungaran & Bandungan.
            </p>
          </div>

          <div className="rounded-3xl bg-[#07111f] p-8 shadow-xl">
            <div className="mb-7 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#d4af37]/15 text-[#e4c45a]">
              <ShieldCheck size={25} />
            </div>

            <h3 className="text-2xl font-bold text-white">
              Keunggulan Layanan NAYVERA
            </h3>

            <div className="mt-7 space-y-4">
              {[
                'Armada prima & tangguh di tanjakan',
                'Opsi lepas kunci atau dengan sopir',
                'Cakupan luas Semarang Kota & Ungaran',
                'Tarif transparan tanpa biaya tersembunyi',
                'Layanan respon cepat WhatsApp 24 jam',
              ].map((item) => (
                <div key={item} className="flex gap-3">
                  <CheckCircle2
                    size={20}
                    className="mt-0.5 shrink-0 text-[#d4af37]"
                  />
                  <span className="text-sm leading-6 text-slate-300">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#07111f] py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
              Layanan NAYVERA di Semarang
            </p>

            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Pilihan Jenis Layanan Rental Mobil
              <span className="text-[#d4af37]"> Sesuai Kebutuhan</span>
            </h2>

            <p className="mt-5 leading-8 text-slate-400">
              Sesuaikan model layanan kendaraan dengan agenda perjalanan Anda di Semarang.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((service) => {
              const Icon = service.icon;

              return (
                <article
                  key={service.title}
                  className="group rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition duration-300 hover:-translate-y-1 hover:border-[#d4af37]/60 hover:bg-white/[0.07]"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#d4af37]/10 text-[#e4c45a] transition group-hover:bg-[#d4af37] group-hover:text-[#07111f]">
                    <Icon size={23} />
                  </div>

                  <h3 className="mt-6 text-xl font-bold text-white">
                    {service.title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-slate-400">
                    {service.description}
                  </p>

                  <a
                    href={createWhatsAppUrl(service.title)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#e4c45a] transition hover:text-white"
                  >
                    Konsultasikan layanan ini
                    <ArrowRight size={16} />
                  </a>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Agenda Perjalanan
            </p>

            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Kendaraan Andal untuk
              <span className="text-[#b89528]"> Berbagai Keperluan</span>
            </h2>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {useCases.map((item) => {
              const Icon = item.icon;

              return (
                <article
                  key={item.title}
                  className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:border-[#d4af37]/50 hover:shadow-lg"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#07111f] text-[#e4c45a]">
                    <Icon size={21} />
                  </div>

                  <h3 className="mt-5 text-lg font-bold text-[#07111f]">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-slate-600">
                    {item.description}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FLEET / ARMADA */}
      <section id="armada" className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div className="max-w-3xl">
              <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
                Katalog Armada
              </p>

              <h2 className="text-3xl font-bold text-[#07111f] sm:text-4xl">
                Pilihan Armada Sewa Mobil
                <span className="text-[#b89528]"> Semarang</span>
              </h2>

              <p className="mt-5 leading-8 text-slate-600">
                Berikut adalah deretan armada terbaik kami yang siap disewa untuk menemani 
                perjalanan Anda di Semarang, Ungaran, dan sekitarnya.
              </p>
            </div>

            <a
              href="/fleet-armada-page"
              className="inline-flex items-center gap-2 font-semibold text-[#07111f] transition hover:text-[#b89528]"
            >
              Lihat Armada Lengkap
              <ArrowRight size={18} />
            </a>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {fleet.map((car) => (
              <article
                key={car.name}
                className="group flex flex-col justify-between overflow-hidden rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[#d4af37]/70 hover:shadow-xl"
              >
                <div>
                  <div className="relative aspect-[16/10] overflow-hidden rounded-2xl bg-slate-100">
                    <img
                      src={car.image}
                      alt={`${car.name} untuk sewa mobil Semarang NAYVERA`}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />

                    <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold text-white shadow-sm">
                      <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                      {car.status}
                    </div>

                    <div className="absolute right-3 top-3 rounded-full bg-[#d4af37] px-3 py-1 text-xs font-bold text-[#07111f] shadow-sm">
                      {car.category}
                    </div>

                    <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-lg bg-black/60 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm">
                      <Users size={13} className="text-[#d4af37]" />
                      {car.capacity}
                    </div>
                  </div>

                  <p className="mt-4 text-xs font-bold uppercase tracking-wider text-[#b89528]">
                    {car.category}
                  </p>

                  <h3 className="mt-1 text-xl font-bold text-[#07111f]">
                    {car.name}
                  </h3>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700">
                      ⚙️ {car.transmission}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700">
                      ⛽ {car.fuel}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {car.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-600"
                      >
                        ✨ {feature}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-6 border-t border-slate-100 pt-4">
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <p className="text-xs text-slate-500">Mulai dari</p>
                      <p className="text-lg font-black text-[#07111f]">{car.price}</p>
                      <p className="text-[11px] text-slate-400">{car.priceLabel}</p>
                    </div>

                    <div className="flex flex-col gap-2">
                      <a
                        href={createWhatsAppUrl(car.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center rounded-xl bg-[#d4af37] px-4 py-2 text-center text-xs font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
                      >
                        Booking
                      </a>
                      <a
                        href={createWhatsAppUrl(car.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-1 rounded-xl border border-emerald-500/40 bg-emerald-50/50 px-3 py-1.5 text-center text-xs font-bold text-emerald-600 transition hover:bg-emerald-500/10"
                      >
                        <MessageCircle size={13} />
                        WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="bg-[#0b1726] py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
              Cakupan Wilayah
            </p>

            <h2 className="mt-3 text-3xl font-bold text-white sm:text-4xl">
              Area Layanan Rental Mobil
              <span className="text-[#d4af37]"> Semarang & Sekitarnya</span>
            </h2>

            <p className="mt-5 leading-8 text-slate-400">
              Kami melayani penjemputan di seluruh titik Kota Semarang, Kabupaten Semarang, 
              stasiun, bandara, serta area kawasan industri.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {areaGroups.map((area) => (
              <article
                key={area.title}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-6"
              >
                <div className="flex items-center gap-2 text-[#e4c45a]">
                  <MapPin size={18} />
                  <h3 className="font-bold text-white">{area.title}</h3>
                </div>

                <p className="mt-3 text-sm leading-6 text-slate-400">
                  {area.description}
                </p>

                <ul className="mt-5 space-y-2.5">
                  {area.places.map((place) => (
                    <li
                      key={place}
                      className="border-b border-white/5 pb-2 text-sm text-slate-400 last:border-0"
                    >
                      {place}
                    </li>
                  ))}
                </ul>

                <a
                  href={createWhatsAppUrl(`Perjalanan area ${area.title}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#e4c45a] transition hover:text-white"
                >
                  Tanyakan area ini
                  <ArrowRight size={15} />
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* AIRPORT & STASIUN */}
      <section className="overflow-hidden bg-[#07111f]">
        <div className="mx-auto grid max-w-7xl lg:grid-cols-2">
          <div className="relative min-h-[380px] lg:min-h-[520px]">
            <img
              src="/assets/images/Armada Hiace Nayvera.jpeg"
              alt="Armada NAYVERA untuk penjemputan Bandara dan Stasiun Semarang"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#07111f]/50 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-[#07111f]" />
          </div>

          <div className="flex items-center px-6 py-20 lg:px-12">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
                Airport & Stasiun Transfer
              </p>

              <h2 className="mt-3 text-3xl font-bold leading-tight text-white sm:text-4xl">
                Penjemputan Bandara Ahmad Yani
                <span className="text-[#d4af37]"> & Stasiun Tawang/Poncol</span>
              </h2>

              <p className="mt-5 leading-8 text-slate-300">
                Tiba di Semarang menggunakan pesawat atau kereta api? NAYVERA siap melayani 
                penjemputan tepat waktu dari Bandara Jenderal Ahmad Yani maupun Stasiun Tawang/Poncol 
                dengan armada bersih dan pengemudi profesional.
              </p>

              <div className="mt-8">
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#d4af37] px-6 py-3.5 font-semibold text-[#07111f] transition hover:bg-[#e4c45a]"
                >
                  <MessageCircle size={18} />
                  Pesan Penjemputan Bandara/Stasiun
                  <ArrowRight size={17} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* UNIQUE SEO ARTICLES SECTION */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Wawasan & Panduan Sewa Mobil
            </p>
            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Artikel & Tips Seputar
              <span className="text-[#b89528]"> Rental Mobil di Semarang</span>
            </h2>
            <p className="mt-4 text-slate-600">
              Informasi lengkap untuk membantu merencanakan liburan wisata, perjalanan bisnis, dan kegiatan keluarga Anda di Semarang.
            </p>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            {articles.map((art) => (
              <article
                key={art.number}
                className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition hover:border-[#d4af37]/60 hover:shadow-md"
              >
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-[#07111f] px-3.5 py-1 text-xs font-bold text-[#e4c45a]">
                    {art.category}
                  </span>
                  <span className="text-2xl font-black text-slate-300">
                    {art.number}
                  </span>
                </div>

                <h3 className="mt-5 text-xl font-bold text-[#07111f]">
                  {art.title}
                </h3>

                <div className="mt-4 space-y-3 text-sm leading-7 text-slate-600">
                  {art.paragraphs.map((p, idx) => (
                    <p key={idx}>{p}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              Kenapa Memilih Kami
            </p>
            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Alasan Percayakan Perjalanan Anda
              <span className="text-[#b89528]"> Kepada NAYVERA</span>
            </h2>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {reasons.map((r) => {
              const Icon = r.icon;
              return (
                <div
                  key={r.title}
                  className="rounded-2xl border border-slate-200 p-6 transition hover:border-[#b89528]"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#07111f] text-[#e4c45a]">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-5 font-bold text-[#07111f]">{r.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{r.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#b89528]">
              FAQ
            </p>
            <h2 className="mt-3 text-3xl font-bold text-[#07111f] sm:text-4xl">
              Pertanyaan yang Sering Diajukan
            </h2>
            <p className="mt-4 text-slate-600">
              Temukan jawaban seputar layanan sewa mobil Semarang bersama NAYVERA.
            </p>
          </div>

          <div className="mt-12 space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition open:border-[#d4af37]"
              >
                <summary className="flex cursor-pointer items-center justify-between font-bold text-[#07111f]">
                  {faq.question}
                  <ChevronDown size={18} className="transition group-open:rotate-180 text-[#b89528]" />
                </summary>
                <p className="mt-4 text-sm leading-7 text-slate-600">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FOOTER BANNER */}
      <section className="bg-[#07111f] py-20 text-white">
        <div className="mx-auto max-w-5xl px-6 text-center lg:px-8">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#e4c45a]">
            Siap Berangkat ke Semarang?
          </p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
            Pesan Kendaraan Anda Sekarang di NAYVERA
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-300">
            Dapatkan pelayanan cepat, ramah, dan transparan untuk setiap kebutuhan perjalanan Anda.
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-[#d4af37] px-8 py-4 font-bold text-[#07111f] transition hover:bg-[#e4c45a]"
            >
              <MessageCircle size={20} />
              Hubungi WhatsApp Sekarang
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </main>
  );
}